import React, { useState, useEffect, useRef } from 'react';
import { Plus, Camera, Save, X, Edit3 } from 'lucide-react';
import toast from 'react-hot-toast';
import Header from './Header';
import ProfileForm from './ProfileForm';
import AddressCard from './AddressCard';
import AddressModal from './AddressModal';
import { useProfile } from '../context/ProfileContext';
import { useAddress } from "../context/AddressContext";
import { CDN_URL } from '../config/apiconfig';

const Profile = () => {
  const [activeSection, setActiveSection] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const { profileData, getProfile, updateProfile, loading } = useProfile();

  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user.id;
  const { addresses, getAddresses, deleteAddress, saveAddress, loading: addressLoading } = useAddress();

  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });

  // Local state for editing
  const [localProfile, setLocalProfile] = useState({
    id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    gender: '',
    image: null,
    status: ''
  });

  useEffect(() => {
    getProfile(userId);
  }, []);

  // Sync localProfile with fetched profileData
  useEffect(() => {
    if (profileData) {
      setLocalProfile(profileData);
    }
  }, [profileData]);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return { isValid: false, message: 'Email is required' };
    }
    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'Please enter a valid email address' };
    }
    return { isValid: true, message: '' };
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9]{10}$/;
    if (!phone) {
      return { isValid: false, message: 'Phone number is required' };
    }
    if (!phoneRegex.test(phone)) {
      return { isValid: false, message: 'Phone number must be exactly 10 digits' };
    }
    return { isValid: true, message: '' };
  };

  const validateName = (name, fieldName) => {
    const nameRegex = /^[a-zA-Z\s]+$/;
    if (!name) {
      return { isValid: false, message: `${fieldName} is required` };
    }
    if (name.length < 2) {
      return { isValid: false, message: `${fieldName} must be at least 2 characters long` };
    }
    if (!nameRegex.test(name)) {
      return { isValid: false, message: `${fieldName} can only contain letters and spaces` };
    }
    return { isValid: true, message: '' };
  };

  // File validation function
  const validateFile = (file) => {
    const maxSize = 2048 * 1024; // 2MB in bytes
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

    // Check file size
    if (file.size > maxSize) {
      return {
        isValid: false,
        message: "Image size must not exceed 2MB. Please choose a smaller image."
      };
    }

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        message: "Please select a valid image file (JPEG, PNG, GIF, or WebP)."
      };
    }

    return { isValid: true };
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate the file before proceeding
    const validation = validateFile(file);
    if (!validation.isValid) {
      toast.error(validation.message);
      // Clear the file input
      e.target.value = '';
      return;
    }

    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const validateAllFields = () => {
    const errors = {};
    let isValid = true;

    // Validate first name
    const firstNameValidation = validateName(localProfile.first_name, 'First name');
    if (!firstNameValidation.isValid) {
      errors.first_name = firstNameValidation.message;
      isValid = false;
    }

    // Validate last name
    const lastNameValidation = validateName(localProfile.last_name, 'Last name');
    if (!lastNameValidation.isValid) {
      errors.last_name = lastNameValidation.message;
      isValid = false;
    }

    // Validate email
    const emailValidation = validateEmail(localProfile.email);
    if (!emailValidation.isValid) {
      errors.email = emailValidation.message;
      isValid = false;
    }

    // Validate phone
    const phoneValidation = validatePhone(localProfile.phone);
    if (!phoneValidation.isValid) {
      errors.phone = phoneValidation.message;
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleSave = async () => {
    if (!localProfile) return;

    // Validate all fields before saving
    if (!validateAllFields()) {
      toast.error('Please fix the validation errors before saving');
      return;
    }

    // Additional validation before sending
    if (selectedImage) {
      const validation = validateFile(selectedImage);
      if (!validation.isValid) {
        toast.error(validation.message);
        return;
      }
    }

    const formData = new FormData();
    formData.append("first_name", localProfile.first_name.trim());
    formData.append("last_name", localProfile.last_name.trim());
    formData.append("email", localProfile.email.trim());
    formData.append("phone", localProfile.phone);
    formData.append("gender", localProfile.gender);

    if (selectedImage) {
      formData.append("image", selectedImage);
    }

    const result = await updateProfile(formData);

    if (result.success) {
      setIsEditing(false);
      setImagePreview(null);
      setSelectedImage(null);
      setValidationErrors({});
      toast.success('Profile updated successfully!');
    } else {
      // Show the specific error message from the server
      toast.error(result.message || "Failed to update profile");
    }
  };

  const handleCancel = () => {
    setLocalProfile(profileData); // Reset changes
    setIsEditing(false);
    setImagePreview(null);
    setSelectedImage(null);
    setValidationErrors({}); // Clear validation errors
  };

  const handleInputChange = (field, value) => {
    let processedValue = value;
    let error = '';

    // Apply field-specific filtering and validation
    switch (field) {
      case 'phone':
        // Remove all non-numeric characters
        processedValue = value.replace(/[^0-9]/g, "");
        // Limit to 10 digits
        if (processedValue.length <= 10) {
          setLocalProfile(prev => ({ ...prev, [field]: processedValue }));
          // Validate phone in real-time
          if (processedValue.length > 0) {
            const phoneValidation = validatePhone(processedValue);
            error = phoneValidation.isValid ? '' : phoneValidation.message;
          }
        } else {
          // Don't update if more than 10 digits
          return;
        }
        break;

      case 'first_name':
      case 'last_name':
        // Remove numbers and special characters, keep only letters and spaces
        processedValue = value.replace(/[^a-zA-Z\s]/g, "");
        setLocalProfile(prev => ({ ...prev, [field]: processedValue }));
        // Validate name in real-time
        if (processedValue.length > 0) {
          const fieldDisplayName = field === 'first_name' ? 'First name' : 'Last name';
          const nameValidation = validateName(processedValue, fieldDisplayName);
          error = nameValidation.isValid ? '' : nameValidation.message;
        }
        break;

      case 'email':
        // Allow email characters, trim spaces
        processedValue = value.trim();
        setLocalProfile(prev => ({ ...prev, [field]: processedValue }));
        // Validate email in real-time
        if (processedValue.length > 0) {
          const emailValidation = validateEmail(processedValue);
          error = emailValidation.isValid ? '' : emailValidation.message;
        }
        break;

      default:
        setLocalProfile(prev => ({ ...prev, [field]: processedValue }));
        break;
    }

    // Update validation errors
    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  useEffect(() => {
    if (addresses.length > 0) {
      const primary = addresses.find(a => a.is_primary === 1);
      setActiveAddressId(primary ? primary.id : addresses[0].id);
    }
  }, [addresses]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    const token = localStorage.getItem("token");

    if (!user?.id || !token) return;

    getAddresses(user.id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteAddress = async (addressId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this address?");
    if (!confirmDelete) return; // user canceled
    await deleteAddress(userId, addressId);
  };

  const [activeAddressId, setActiveAddressId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  const colorSchemes = ['blue', 'purple', 'green', 'orange'];

  const handleEdit = () => {
    setIsEditing(true);
    setValidationErrors({}); // Clear any existing errors when starting to edit
  };
  const handleAddressSelect = id => setActiveAddressId(id);
  const handleAddAddress = () => { setEditingAddress(null); setIsModalOpen(true); };
  const handleEditAddress = address => { setEditingAddress(address); setIsModalOpen(true); };

  const handleSaveAddress = async (formData) => {
    // Validate phone number if provided
    if (formData.phone && !/^[0-9]{10}$/.test(formData.phone)) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }

    // Base payload
    const payload = {
      user_id: userId,
      address: formData.name,
      type: formData.type,
      street: formData.street,
      city: formData.city,
      state: formData.state,
      zipcode: formData.zipCode,
      phone: formData.phone || "",
      status: "Active",
      is_primary: "1",
      create_audit_id: editingAddress ? undefined : userId, // only for new
      update_audit_id: editingAddress ? userId : undefined, // only for edit
    };

    // Remove undefined fields (optional, keeps payload clean)
    Object.keys(payload).forEach(
      (key) => payload[key] === undefined && delete payload[key]
    );

    const result = editingAddress
      ? await saveAddress(payload, editingAddress.id)
      : await saveAddress(payload);

    if (result.success) {
      // Fetch latest addresses from API/context
      await getAddresses(userId);

      // Close modal
      setIsModalOpen(false);

      // Show success toast
      toast.success(editingAddress ? 'Address updated successfully!' : 'Address added successfully!');
    } else {
      toast.error(result.message || "Failed to save address");
    }
  };

  // Enhanced address modal save handler with phone validation
  const handleAddressModalSave = (formData) => {
    // Apply phone validation to address phone field
    if (formData.phone) {
      const numericPhone = formData.phone.replace(/[^0-9]/g, "");
      if (numericPhone.length !== 10) {
        toast.error("Phone number must be exactly 10 digits");
        return;
      }
      formData.phone = numericPhone;
    }

    handleSaveAddress(formData);
  };

  const fileInputRef = useRef(null);

  return (
    <div className="relative mx-auto my-0 bg-orange-50 bg-opacity-90 h-auto pb-8 w-full">
      {/* Header */}
      <Header activeSection={activeSection} />

      {/* Main Profile Content */}
      <main className="relative mx-auto w-[80%] max-md:w-[90%] mt-2">
        {/* Profile Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Profile Header Section */}
          <section className="relative p-6 border-b border-gray-100" aria-labelledby="profile-heading">
            <div className="flex items-center justify-between max-sm:flex-col max-sm:gap-4">
              {/* Profile Image and Info */}
              <div className="flex items-center gap-6 max-sm:flex-col max-sm:text-center">
                <div className="relative">
                  <img
                    src={
                      imagePreview
                        ? imagePreview
                        : `${CDN_URL}/${localProfile.image}`
                    }
                    alt={localProfile.first_name}
                    className="w-[100px] h-[100px] rounded-full object-cover shadow-md"
                  />
                  {/* Camera icon - only visible in edit mode */}
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  {isEditing && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current.click()}
                      className="absolute bottom-0 right-0 w-8 h-8 bg-teal-800 rounded-full flex items-center justify-center text-white hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 shadow-lg"
                      aria-label="Change profile picture"
                      title="Upload image (Max 2MB, JPEG/PNG/GIF/WebP only)"
                    >
                      <Camera className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div>
                  <h1
                    id="profile-heading"
                    className="text-xl font-medium text-black max-sm:text-lg"
                  >
                    {localProfile.first_name} {localProfile.last_name}
                  </h1>
                  <p className="text-base text-black opacity-50 mt-2 max-sm:text-sm">
                    {localProfile.email}
                  </p>
                  {localProfile.phone && (
                    <p className="text-sm text-gray-600 mt-1">
                      Phone: {localProfile.phone}
                    </p>
                  )}
                </div>
              </div>

              {/* Edit/Save/Cancel Buttons */}
              <div className="flex gap-2">
                {!isEditing ? (
                  <button
                    onClick={handleEdit}
                    className="flex items-center gap-2 bg-teal-800 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 max-sm:px-4 max-sm:py-2 max-sm:text-sm"
                    aria-label="Edit profile information"
                  >
                    <Edit3 className="w-4 h-4" />
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleSave}
                      className="flex items-center gap-2 bg-teal-800 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 max-sm:px-4 max-sm:py-2 max-sm:text-sm"
                      aria-label="Save changes"
                    >
                      <Save className="w-4 h-4" />
                      Save
                    </button>
                    <button
                      onClick={handleCancel}
                      className="flex items-center gap-2 bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 max-sm:px-4 max-sm:py-2 max-sm:text-sm"
                      aria-label="Cancel editing"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </section>

          {/* Profile Form */}
          <ProfileForm
            profileData={localProfile}
            isEditing={isEditing}
            onInputChange={handleInputChange}
            validationErrors={validationErrors}
          />

          {/* Address Management Section */}
          <section className="p-6 border-t border-gray-100" aria-labelledby="address-section-heading">
            <div className="flex items-center justify-between mb-6">
              <h2 id="address-section-heading" className="text-lg font-medium text-black">
                My Addresses
              </h2>
              <button
                onClick={handleAddAddress}
                className="flex items-center gap-2 bg-teal-800 bg-opacity-10 text-teal-800 px-4 py-2 rounded-lg hover:bg-teal-800 hover:bg-opacity-20 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                aria-label="Add new address"
              >
                <Plus className="w-4 h-4" aria-hidden="true" />
                Add Address
              </button>
            </div>

            {/* Address Cards Grid */}
            {addressLoading ? (
              <div className="text-center py-8 text-gray-600">
                Loading addresses...
              </div>
            ) : addresses.length === 0 ? (
              <div className="text-center py-8 text-gray-600">
                <p className="mb-4">No addresses found. Add your first address to get started.</p>
                <button
                  onClick={handleAddAddress}
                  className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Add Your First Address
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {addresses.map((address, index) => (
                  <AddressCard
                    key={address.id}
                    address={address}
                    isActive={address.id === activeAddressId}
                    colorScheme={colorSchemes[index % colorSchemes.length]}
                    onSelect={() => handleAddressSelect(address.id)}
                    onEdit={handleEditAddress}
                    onDelete={handleDeleteAddress}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </main>

      {/* Address Modal */}
      <AddressModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleAddressModalSave}
        editingAddress={editingAddress}
      />
    </div>
  );
};

export default Profile;
